import config from 'config';
import type { ShippingModel, User } from 'core';
import type { SelectorReturnFn } from 'core/use-cases/utilities';
import type { Available, Delivery } from 'core/domains/shippingModel';

import { SHIPPING_MODEL } from 'core/domains';
import { useSelector } from 'core/use-cases/utilities';

export function useUserSelector(): SelectorReturnFn<User['data']> {
  const { data, status, updatedAt = 0 } = useSelector((state) => state.user);

  return {
    data,
    status,
    updatedAt,
  };
}

export function useUserCreditSelector(): SelectorReturnFn<User['credit']['data']> {
  const { data, status, updatedAt = 0 } = useSelector((state) => state.user.credit);

  return {
    data,
    status,
    updatedAt,
  };
}

export function useUserBuyIntentionSelector(): User['buyIntention']['items'] {
  const {
    buyIntention: { items },
  } = useSelector(({ user }) => user);

  return items;
}

export function useShippingModelSelector(): Pick<ShippingModel, 'toggleActive' | 'data'> {
  const { toggleActive, data } = useSelector(({ shippingModel }) => shippingModel);

  return { data, toggleActive };
}

export function useUserSearchInputSelector(): User['shippingModelSearch'] {
  const { shippingModelSearch } = useSelector((state) => state.user);

  return shippingModelSearch;
}

export function useItemAddCartErrorSelector(): User['itemAddCartError'] {
  const { itemAddCartError } = useSelector(({ user }) => user);

  return itemAddCartError;
}

export function useAvailablesDeliveries() {
  const { data, toggleActive } = useShippingModelSelector();

  const userShippingModelAvailables = useSelector(({ user }) =>
    user.data.shippingModel?.availables ? user.data.shippingModel?.availables : [],
  );

  const filterAvailables = (model: Available) =>
    [config.pickupStore, SHIPPING_MODEL.KEY_ACCOUNT_PICKUP_STORE].some(
      (shippingModels) => shippingModels === model.code,
    );

  let withdrawal = [];

  if (toggleActive) {
    withdrawal = data.availables?.filter((model) => filterAvailables(model));
  } else {
    withdrawal = userShippingModelAvailables?.length
      ? userShippingModelAvailables.filter((model) => filterAvailables(model))
      : [];
  }

  return withdrawal?.length
    ? withdrawal[0].deliveries.map((delivery: Delivery) => delivery.isWithdrawalCenter && delivery)
    : withdrawal;
}

export function useIsResaleIntention() {
  return useSelector(({ user }) => user.data.isResaleIntention);
}

export function useIsAutoStartEnabled() {
  return useSelector(({ user }) => user.data.isAutoStartEnabled);
}

export function useIncentiveCampaigns(): SelectorReturnFn<User['incentiveCampaigns']['data']> {
  const { data, status, updatedAt = 0 } = useSelector((state) => state.user.incentiveCampaigns);

  return {
    data,
    status,
    updatedAt,
  };
}
